/* eslint-disable sonarjs/no-duplicate-string */
import styled, { CSSProperties } from 'styled-components';

import { NorlysFont } from '~src/branding';
import { useBranding } from '~src/context/BrandingContext';
import { useDisplayContext } from '~src/context/DisplayContext';
import { usePublicCalculatorContext } from '~src/context/PublicCalculatorContext';
import { useSelectedProductTypesContext } from '~src/context/selectedProductTypeContext';

import { Button, ButtonNorlys } from '../Button';
import { SwipeButton, SwipeButtonNorlys } from '../SwipeButton';
import { Heading } from '../Typography';
import { IconWrapper, TextWrapper } from './style';
import { Takeover } from './Takeover';
import { TakeoverNorlys } from './Takeover/norlys';
import { InfoBoxProps } from './types';
import { infoBoxColorAndSize } from './util';

const Wrapper = styled.div<{ direction: CSSProperties['flexDirection']; topPadding?: boolean }>(
  ({ direction, topPadding: applyTopPadding }) => {
    const isColumn = direction === 'column';
    const topPadding = applyTopPadding ? '40px' : '8px';

    return {
      display: 'flex',
      flexFlow: `${direction} nowrap`,
      alignItems: isColumn ? 'center' : undefined,
      height: '100%',
      justifyContent: 'space-around',
      borderRadius: '24px',
      width: '320px',
      minWidth: '260px',
      padding: isColumn ? `${topPadding}px 24px 16px 24px` : '24px 40px',
    };
  }
);

const ItemWrapper = styled.div<{ direction: CSSProperties['flexDirection']; isLast: boolean }>(
  ({ direction, isLast }) => ({
    display: 'flex',
    alignItems: 'center',
    marginRight: direction === 'column' ? '0' : '64px',
    marginBottom: direction === 'column' && !isLast ? '16px' : '0',
  })
);

export const InfoBox = ({
  entries,
  type = 'default',
  color,
  direction = 'column',
  button,
  children,
  style,
  itemStyle,
  title,
  alignHeader = 'flex-start',
  residence,
}: InfoBoxProps) => {
  const { isMobile } = useDisplayContext();
  const { darkColor, lightTextColor } = useBranding();
  const { selectedProductTypes } = useSelectedProductTypesContext();

  const { takeover } = usePublicCalculatorContext();

  const { backgroundColor = darkColor, textColor = lightTextColor } = infoBoxColorAndSize(color, type);

  const heatPumpDisabled = selectedProductTypes.includes('heatPump') && !!takeover;
  const solarDisabled = selectedProductTypes.includes('solar') && takeover === 'apartment';
  const bothDisabled = selectedProductTypes.length > 1 && !!takeover;

  if (heatPumpDisabled || solarDisabled || bothDisabled) {
    return (
      <Wrapper {...{ direction, style: { ...style, backgroundColor, padding: '24px 24px 16px' } }}>
        <Takeover takeover={takeover} residence={residence} />
      </Wrapper>
    );
  }

  return (
    <Wrapper {...{ direction, topPadding: !!title, style: { ...style, backgroundColor } }}>
      {title && (
        <Heading level={1} color='light' style={{ alignSelf: alignHeader, marginBottom: '16px' }}>
          {title}
        </Heading>
      )}

      {children}

      <div
        {...{
          style: {
            display: 'flex',
            flexDirection: direction,
            width: direction === 'column' ? 'fit-content' : '100%',
            justifyContent: 'space-between',
          },
        }}
      >
        {entries.map(({ label, Icon, value, valueLevel }, index) => (
          <ItemWrapper style={itemStyle} key={label} direction={direction} isLast={entries.length === index + 1}>
            {Icon && <IconWrapper>{<Icon width={28} height={25} color={textColor} />}</IconWrapper>}

            <TextWrapper>
              <Heading level={isMobile ? 3 : 4} color='light' style={{ marginBottom: '4px' }}>
                {label}
              </Heading>

              <Heading level={valueLevel ?? isMobile ? 1 : 2} color='light'>
                {value}
              </Heading>
            </TextWrapper>
          </ItemWrapper>
        ))}
      </div>

      <InfoBoxButton button={button} />
    </Wrapper>
  );
};

export const InfoBoxNorlys = ({
  entries,
  direction = 'column',
  button,
  children,
  style,
  residence,
}: Omit<InfoBoxProps, 'type' | 'color'>) => {
  const { takeover } = usePublicCalculatorContext();

  const backgroundColor = 'white';
  const textColor = 'black';

  const headerTextStyle = { ...NorlysFont.style('default', 16), color: textColor };
  const bodyTextStyle = { ...NorlysFont.style('heading', 26), color: textColor };

  if (takeover) {
    return (
      <Wrapper {...{ direction, style: { backgroundColor, justifyContent: 'flex-start', ...style } }}>
        <TakeoverNorlys takeover={takeover} residence={residence} />
      </Wrapper>
    );
  }

  return (
    <Wrapper {...{ direction, style: { backgroundColor, justifyContent: 'flex-start', ...style } }}>
      {children}
      <div
        {...{
          style: {
            display: 'flex',
            flexDirection: direction,
            width: direction === 'column' ? 'fit-content' : '100%',
            justifyContent: 'space-between',
          },
        }}
      >
        {entries.map((entry, index) => (
          <ItemWrapper key={entry.label} direction={direction} isLast={entries.length === index + 1}>
            <TextWrapper>
              <div style={headerTextStyle}>{entry.label}</div>
              <div style={bodyTextStyle}>{entry.value}</div>
            </TextWrapper>
          </ItemWrapper>
        ))}
      </div>
      <InfoBoxButtonNorlys button={button} />
    </Wrapper>
  );
};

const InfoBoxButton = ({ button }: Pick<InfoBoxProps, 'button'>) => {
  if (!button) {
    return <></>;
  }

  const { onClick, label, disabled, type } = button;

  if (type === 'swipe') {
    return <SwipeButton {...{ onConfirm: onClick, text: label, disabled: disabled }} style={{ marginTop: '24px' }} />;
  }

  return (
    <Button {...{ ...button, type: type, disabled: disabled, size: 'large' }} style={{ marginTop: '24px' }}>
      {label}
    </Button>
  );
};

const InfoBoxButtonNorlys = ({ button }: Pick<InfoBoxProps, 'button'>) => {
  if (!button) {
    return <></>;
  }

  const { onClick, label, disabled, type } = button;

  return type === 'swipe' ? (
    <SwipeButtonNorlys {...{ onConfirm: onClick, text: label, disabled: disabled }} style={{ margin: '16px' }} />
  ) : (
    <ButtonNorlys {...{ ...button, type, disabled: disabled, size: 'large' }} style={{ marginTop: '24px' }}>
      {label}
    </ButtonNorlys>
  );
};
